import jsPDF from 'jspdf';
import imageCompression from 'browser-image-compression';

import logoConnect10 from '../assets/logo-connect10.png';

/**
 * @file Contém a lógica para gerar relatórios fotográficos em PDF.
 */

// --- Funções Auxiliares de Imagem (Módulo) ---

/**
 * Comprime uma imagem a partir de uma URL.
 * @param {string} imageUrl - A URL da imagem a ser comprimida.
 * @returns {Promise<string>} Uma Promise que resolve para uma Object URL da imagem comprimida.
 */
const compressImage = async (imageUrl) => {
    try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const imageFile = new File([blob], "photo.jpg", { type: blob.type });

        const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            maxIteration: 10,
            exifOrientation: true,
            fileType: 'image/jpeg',
        };

        const compressedFile = await imageCompression(imageFile, options);

        // Converte o blob do arquivo comprimido para um Data URL (Base64)
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                resolve(reader.result);
            };
            reader.onerror = (error) => {
                console.error('Erro ao ler o arquivo comprimido:', error);
                reject(error);
            };
            reader.readAsDataURL(compressedFile);
        });
    } catch (error) {
        console.error('Erro ao comprimir imagem:', error);
        throw error;
    }
};

/**
 * Carrega uma imagem a partir de uma URL, aplicando compressão.
 * @param {string} src - A URL da imagem original.
 * @returns {Promise<HTMLImageElement>} Uma Promise que resolve para o elemento da imagem carregada.
 */
const loadImage = (src) => new Promise(async (resolve, reject) => {
    try {
        const compressedDataUrl = await compressImage(src);
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = compressedDataUrl;
    } catch (error) {
        reject(error);
    }
});

/**
 * Converte uma imagem local (importada) para um Data URL (Base64).
 * @param {string} src - O caminho da imagem importada.
 * @param {number} width - A largura desejada da imagem.
 * @param {number} height - A altura desejada da imagem.
 * @returns {Promise<string>} Uma Promise que resolve para o Data URL da imagem.
 */
const imageToDataUrl = (src, width, height) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (err) => {
        console.error("Erro ao converter imagem local para Data URL:", err);
        reject(err);
    };
    img.src = src;
});


/**
 * Gera um relatório fotográfico em PDF para uma determinada troca.
 * @param {object} tradeData - O objeto contendo os dados da troca.
 * @param {Array<object>} photos - A lista de objetos de foto, cada um com 'url' e 'createdBy'.
 * @param {Map<string, object>} usersMap - O mapa de usuários para buscar o nome do técnico.
 */
export const generateTradeReportPDF = async (tradeData, photos, usersMap) => {
    if (!photos || photos.length === 0) {
        console.error("Nenhuma foto fornecida para o relatório.");
        return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');
    const margin = 25;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - (margin * 2);
    const contentHeight = pageHeight - (margin * 2);

    const addHeaderFooter = (logoDataUrl, pageNumber, totalPages) => {
        doc.addImage(logoDataUrl, 'PNG', margin, 10, 40, 10);
        doc.setDrawColor(200);
        doc.line(margin, 22, pageWidth - margin, 22);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        const pageStr = `Página ${pageNumber} de ${totalPages}`;
        const textWidth = doc.getStringUnitWidth(pageStr) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        doc.text(pageStr, (pageWidth - textWidth) / 2, pageHeight - 10);
    };

    try {
        const logoDataUrl = await imageToDataUrl(logoConnect10, 150, 38);
        const loadedImages = await Promise.all(photos.map(p => loadImage(p.url)));
        let photoIndex = 0;
        let pageCounter = 0;
        const totalPages = loadedImages.length + 1;

        while (photoIndex < loadedImages.length) {
            pageCounter++;
            doc.addPage();
            addHeaderFooter(logoDataUrl, pageCounter, totalPages);
            doc.setTextColor(0, 0, 0);

            const img = loadedImages[photoIndex];
            const aspectRatio = img.width / img.height;
            let imgWidth, imgHeight;

            if (contentWidth / aspectRatio <= contentHeight) {
                imgWidth = contentWidth;
                imgHeight = contentWidth / aspectRatio;
            } else {
                imgHeight = contentHeight;
                imgWidth = contentHeight * aspectRatio;
            }

            const x = (pageWidth - imgWidth) / 2;
            const y = (pageHeight - imgHeight) / 2;
            doc.addImage(img, 'JPEG', x, y, imgWidth, imgHeight, undefined, 'SLOW');
            photoIndex++;
        }

        pageCounter++;
        doc.addPage();
        addHeaderFooter(logoDataUrl, pageCounter, totalPages);
        doc.setTextColor(0, 0, 0);
        
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Relatório de Conclusão de Serviço", margin, margin + 10);
        
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        
        let currentY = margin + 20;

        const addTextSection = (text) => {
            const splitText = doc.splitTextToSize(text, contentWidth);
            doc.text(splitText, margin, currentY);
            currentY += (doc.getTextDimensions(splitText).h) + 10;
        };
        
        const reportDate = tradeData.servicePeriodStart ? new Date(tradeData.servicePeriodStart).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR');
        addTextSection(`Declaramos para os devidos fins que o serviço referente à Nota de Serviço (NS) ${tradeData.serviceNote}, realizado no endereço ${tradeData.address}, foi acompanhado por uma equipe técnica da CONNECT10 INTERNET em ${reportDate}.`);
        addTextSection("Ao final da intervenção, foi verificado que não restaram cabos ou quaisquer outros materiais soltos ou pendentes pertencentes à nossa empresa no local, garantindo a segurança e a organização da infraestrutura.");

        if (tradeData.technicianNotes && tradeData.technicianNotes.trim() !== '') {
            doc.setFont("helvetica", "bold");
            doc.text("Observações do Técnico:", margin, currentY);
            currentY += 7;
            doc.setFont("helvetica", "normal");
            addTextSection(tradeData.technicianNotes);
        }

        const initialDateObj = tradeData.servicePeriodStart ? new Date(tradeData.servicePeriodStart) : new Date();
        const dateString = `${tradeData.city}, ${initialDateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}`;
        currentY += 5;
        doc.text(dateString, margin, currentY);
        currentY += 20;

        let technicianName = "";
        if (photos.length > 0 && photos[0].createdBy && usersMap) {
            const firstPhotoUser = usersMap[photos[0].createdBy];
            if (firstPhotoUser?.displayName) {
                technicianName = firstPhotoUser.displayName;
            }
        }
        
        doc.text(technicianName, margin, currentY);
        doc.line(margin, currentY + 2, margin + 80, currentY + 2);
        currentY += 7;
        doc.text("CONNECT10 INTERNET LTDA", margin, currentY);
        currentY += 5;
        doc.text("CNPJ 05.502.768/0001-87", margin, currentY);

        doc.deletePage(1);
        doc.save(`Relatorio_NS_${tradeData.serviceNote}.pdf`);

    } catch (error) {
        console.error("Erro ao gerar PDF:", error);
        throw new Error("Falha ao gerar o relatório em PDF. Verifique o console para mais detalhes.");
    }
};

/**
 * Gera um relatório fotográfico em PDF para uma notificação.
 * @param {object} notificationData - Os dados da notificação.
 * @param {Array<object>} photos - A lista de fotos.
 * @param {Map<string, object>} usersMap - O mapa de usuários.
 */
export const generateNotificationReportPDF = async (notificationData, photos, usersMap) => {
    if (!photos || photos.length === 0) {
        console.error("Nenhuma foto fornecida para o relatório.");
        return;
    }

    const doc = new jsPDF('p', 'mm', 'a4');
    const margin = 25;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - (margin * 2);
    const contentHeight = pageHeight - (margin * 2);

    const addHeaderFooter = (logoDataUrl, pageNumber, totalPages) => {
        doc.addImage(logoDataUrl, 'PNG', margin, 10, 40, 10);
        doc.setDrawColor(200);
        doc.line(margin, 22, pageWidth - margin, 22);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        const pageStr = `Página ${pageNumber} de ${totalPages}`;
        const textWidth = doc.getStringUnitWidth(pageStr) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        doc.text(pageStr, (pageWidth - textWidth) / 2, pageHeight - 10);
    };

    try {
        const logoDataUrl = await imageToDataUrl(logoConnect10, 150, 38);
        const loadedImages = await Promise.all(photos.map(p => loadImage(p.url)));
        let photoIndex = 0;
        let pageCounter = 0;
        const totalPages = loadedImages.length + 1;

        while (photoIndex < loadedImages.length) {
            pageCounter++;
            doc.addPage();
            addHeaderFooter(logoDataUrl, pageCounter, totalPages);
            doc.setTextColor(0, 0, 0);

            const img = loadedImages[photoIndex];
            const aspectRatio = img.width / img.height;
            let imgWidth, imgHeight;

            if (contentWidth / aspectRatio <= contentHeight) {
                imgWidth = contentWidth;
                imgHeight = contentWidth / aspectRatio;
            } else {
                imgHeight = contentHeight;
                imgWidth = contentHeight * aspectRatio;
            }

            const x = (pageWidth - imgWidth) / 2;
            const y = (pageHeight - imgHeight) / 2;
            doc.addImage(img, 'JPEG', x, y, imgWidth, imgHeight, undefined, 'SLOW');
            photoIndex++;
        }

        pageCounter++;
        doc.addPage();
        addHeaderFooter(logoDataUrl, pageCounter, totalPages);
        doc.setTextColor(0, 0, 0);

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("Relatório Fotográfico de Notificação", margin, margin + 10);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        let currentY = margin + 20;

        const addTextSection = (text) => {
            const splitText = doc.splitTextToSize(text, contentWidth);
            doc.text(splitText, margin, currentY);
            currentY += (doc.getTextDimensions(splitText).h) + 10;
        };

        addTextSection(`Declaramos para os devidos fins que o serviço referente à Notificação ${notificationData.notificationNumber} foi executado com sucesso, não sendo identificadas quaisquer irregularidades da parte da CONNECT10 após a intervenção.`);

        if (notificationData.technicianNotes && notificationData.technicianNotes.trim() !== '') {
            doc.setFont("helvetica", "bold");
            doc.text("Observações do Técnico:", margin, currentY);
            currentY += 7;
            doc.setFont("helvetica", "normal");
            addTextSection(notificationData.technicianNotes);
        }

        const closedAtDate = notificationData.closedAt?.toDate ? notificationData.closedAt.toDate() : new Date();
        const dateString = `${notificationData.city}, ${closedAtDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}`;
        currentY += 5;
        doc.text(dateString, margin, currentY);
        currentY += 20;

        let technicianName = "";
        if (notificationData.updatedBy && usersMap) {
            const closingUser = usersMap.get(notificationData.updatedBy);
            if (closingUser?.displayName) {
                technicianName = closingUser.displayName;
            }
        }

        doc.text(technicianName, margin, currentY);
        doc.line(margin, currentY + 2, margin + 80, currentY + 2);
        currentY += 7;
        doc.text("CONNECT10 INTERNET LTDA", margin, currentY);
        currentY += 5;
        doc.text("CNPJ 05.502.768/0001-87", margin, currentY);

        doc.deletePage(1);

        doc.save(`Relatorio_Notificacao_${notificationData.notificationNumber}.pdf`);

    } catch (error) {
        console.error("Erro ao gerar PDF da notificação:", error);
        throw new Error("Falha ao gerar o relatório em PDF. Verifique o console para mais detalhes.");
    }
};
